import { useState, useCallback } from "react";
import { API_CORE } from "../../../../API/ApiCORE";
import { useUserAuthenticated } from "../../Contexts/_Auth/userAuthenticatedContext";
import { REACT_HOT_TOAST_ERROR, REACT_HOT_TOAST_SUCCESS } from "../../../../HELPERS/reactHotToast.helper";
import { isErrorStatusResponse, isSuccessStatusResponse, isWarningStatusResponse } from "../../../../API/_ApiUtil/statusResponseForMessages";

const METHOD_HANDLER = {
    POST: API_CORE.SERVICES.POST_RESOURCE,
    PUT: API_CORE.SERVICES.UPDATE_resource,
    DELETE: API_CORE.SERVICES.DELETE_resource,
    PATCH: API_CORE.SERVICES.PATCH_resource,
};

const METHODS_WITH_ID = new Set(["PUT", "DELETE", "PATCH"]);

const DEFAULT_LOGOUT_ON_401 = String(process.env.REACT_APP_AUTH_LOGOUT_ON_401 ?? "false").trim().toLowerCase() === "true";

const onErrorText = (message, detail, requestId, fields = null) => {
    const firstFieldError = fields && typeof fields === "object"
        ? Object.values(fields).find(Boolean)
        : null;
    const parts = [
        message,
        firstFieldError && firstFieldError !== message ? firstFieldError : null,
        !firstFieldError && detail && detail !== message ? detail : null,
    ].filter(Boolean);
    if (requestId) parts.push(`Referencia: ${requestId}`);
    return parts.join("\n");
};

export const useApiActionMutation = ({ minTimeMutate = 2000, isToastError = false, isToastSuccess = false, logoutOn401 = DEFAULT_LOGOUT_ON_401 } = {}) => {
    const { onCallbackCloseSessionUserAuth } = useUserAuthenticated();
    const [useActionApiIsLoading, setActionApiIsLoading] = useState(false);

    const CB__useActionMutate = useCallback(async ({
        method = "POST", endpoint = "", data = null, id = null, config = {}, successMessage = null,
    }) => {
        const startMutate = Date.now();
        setActionApiIsLoading(true);

        const keepMinTime = async () => {
            const elapsed = Date.now() - startMutate;
            if (elapsed < minTimeMutate) {
                await new Promise((resolve) => setTimeout(resolve, minTimeMutate - elapsed));
            }
        };

        const ACTION_HANDLER = METHOD_HANDLER[method];
        const RESOLVED_ENDPOINT = API_CORE.ENDPOINTS[endpoint];

        if (!ACTION_HANDLER || !RESOLVED_ENDPOINT) {
            await keepMinTime();
            setActionApiIsLoading(false);
            const message = !ACTION_HANDLER ? `Método inválido: ${method}` : `Endpoint inválido: ${endpoint}`;
            if (isToastError) REACT_HOT_TOAST_ERROR(message);
            return { code_status: 400, success: false, data: null, message, detail: null, fields: null };
        }

        let RESPONSE;
        try {
            RESPONSE = METHODS_WITH_ID.has(method)
                ? await ACTION_HANDLER(RESOLVED_ENDPOINT, id, data, config)
                : await ACTION_HANDLER(RESOLVED_ENDPOINT, data, config);
        } catch (error) {
            await keepMinTime();
            setActionApiIsLoading(false);

            const status = error?.response?.status || 500;
            const message = error?.response?.data?.message || error?.message || "Error de red";
            const detail = error?.response?.data?.detail || null;
            const requestId = error?.response?.data?.requestId || null;
            const fields = error?.response?.data?.errors || null;

            if (isToastError) REACT_HOT_TOAST_ERROR(onErrorText(message, detail, requestId, fields), { duration: 7000 });
            if (status === 401 && logoutOn401) onCallbackCloseSessionUserAuth();

            return { code_status: status, success: false, data: null, message, detail, requestId, fields, code: "CLIENT_ERROR" };
        }

        await keepMinTime();
        setActionApiIsLoading(false);

        const CODE_STATUS = Number(
            RESPONSE?.status
            || RESPONSE?.code_status
            || RESPONSE?.data?.status
            || RESPONSE?.statusCode
            || RESPONSE?.CODE_STATUS
            || (RESPONSE?.success === true ? 200 : 500),
        );

        const SUCCESS = RESPONSE?.success === true
            || RESPONSE?.ok === true
            || (CODE_STATUS >= 200 && CODE_STATUS < 300);

        // En error HANDLE_AXIOS_ERROR conserva el body del backend dentro de data.
        // En success, data ya es el payload real del ResponseInterceptor.
        const ERROR_BODY = !SUCCESS && RESPONSE?.data?.success === false ? RESPONSE.data : null;
        const DATA = ERROR_BODY ? (ERROR_BODY.data ?? null) : (RESPONSE?.data ?? null);
        const MESSAGE = RESPONSE?.message || ERROR_BODY?.message || RESPONSE?.msg || "";
        const DETAIL = RESPONSE?.detail || ERROR_BODY?.detail || null;
        const FIELDS = RESPONSE?.errors || ERROR_BODY?.errors || null;
        const REQUEST_ID = RESPONSE?.requestId || ERROR_BODY?.requestId || null;
        const CODE = RESPONSE?.code || ERROR_BODY?.code || null;

        const RESULT = {
            code_status: CODE_STATUS,
            success: SUCCESS,
            data: DATA,
            message: MESSAGE,
            detail: DETAIL,
            fields: FIELDS,
            errors: FIELDS,
            requestId: REQUEST_ID,
            code: CODE,
            meta: {
                verificationRequired: RESPONSE?.meta?.verificationRequired || RESPONSE?.data?.meta,
            },
        };

        if (isErrorStatusResponse(CODE_STATUS) || isWarningStatusResponse(CODE_STATUS) || !SUCCESS) {
            if (isToastError) {
                REACT_HOT_TOAST_ERROR(
                    onErrorText(MESSAGE || "No se pudo completar la operación.", DETAIL, REQUEST_ID, FIELDS),
                    { duration: 7000 },
                );
            }
            if (CODE_STATUS === 401 && logoutOn401) onCallbackCloseSessionUserAuth();
            return RESULT;
        }

        if (isSuccessStatusResponse(CODE_STATUS)) {
            const okMessage = successMessage ?? MESSAGE;
            if (isToastSuccess && okMessage) REACT_HOT_TOAST_SUCCESS(okMessage);
            return RESULT;
        }

        return RESULT;
    }, [minTimeMutate, isToastError, isToastSuccess, logoutOn401, onCallbackCloseSessionUserAuth]);

    return { actionMutate: CB__useActionMutate, useActionApiIsLoading };
};
